import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Save, RefreshCw, Info, ToggleLeft, ToggleRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

interface TicketAllocationSettingsProps {
  eventId?: string; // Optional event ID for event-specific settings
}

export default function TicketAllocationSettings({ eventId }: TicketAllocationSettingsProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    ticket_base_amount: '',
    ticket_additional_amount: '',
    ticket_max_amount: '',
    wristband_per_category: '',
    wristband_max_amount: '',
    tool_car_max_amount: ''
  });
  const [ticketsEnabled, setTicketsEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [eventId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // If eventId is provided, fetch event-specific settings
      if (eventId) {
        // First check if tickets are enabled for this event
        const { data: ticketEnabledData, error: ticketEnabledError } = await supabase
          .from('event_settings')
          .select('setting_value')
          .eq('event_id', eventId)
          .eq('setting_key', 'tickets_enabled')
          .maybeSingle();
          
        if (!ticketEnabledError && ticketEnabledData) {
          setTicketsEnabled(ticketEnabledData.setting_value === 'true');
        } else {
          // If no setting exists, default to false
          setTicketsEnabled(false);
        }
        
        // Fetch all event-specific settings
        const { data: eventSettings, error: eventError } = await supabase
          .from('event_settings')
          .select('setting_key, setting_value')
          .eq('event_id', eventId)
          .in('setting_key', [
            'ticket_base_amount',
            'ticket_additional_amount',
            'ticket_max_amount',
            'wristband_per_category',
            'wristband_max_amount',
            'tool_car_max_amount'
          ]);

        if (!eventError && eventSettings && eventSettings.length > 0) {
          // Convert event settings to the expected format
          const settingsObj = { ...settings };
          
          eventSettings.forEach(setting => {
            settingsObj[setting.setting_key as keyof typeof settings] = setting.setting_value;
          });
          
          setSettings(settingsObj);
        }
      }
    } catch (error) {
      console.error('Error fetching ticket allocation settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      // Validate inputs are positive integers
      for (const [key, value] of Object.entries(settings)) {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 0) {
          toast.error(`${key.replace(/_/g, ' ')} must be a positive number`);
          setSaving(false);
          return;
        }
      }

      if (eventId) {
        // First save the tickets_enabled setting for this event
        const { error: enabledError } = await supabase
          .from('event_settings')
          .upsert({
            event_id: eventId,
            setting_key: 'tickets_enabled',
            setting_value: ticketsEnabled.toString(),
            description: 'Whether tickets and wristbands are enabled for this event'
          }, { 
            onConflict: 'event_id,setting_key' 
          });

        if (enabledError) throw enabledError;

        // If tickets are disabled, remove all ticket-related settings except tickets_enabled
        if (!ticketsEnabled) {
          const { error: deleteError } = await supabase
            .from('event_settings')
            .delete()
            .eq('event_id', eventId)
            .in('setting_key', [
              'ticket_base_amount',
              'ticket_additional_amount',
              'ticket_max_amount',
              'wristband_per_category',
              'wristband_max_amount',
              'tool_car_max_amount'
            ]);

          if (deleteError) throw deleteError;
          
          toast.success('Event ticket settings saved successfully');
          setSaving(false);
          return;
        }

        // Prepare upsert data for event settings
        const upsertData = Object.entries(settings).map(([key, value]) => ({
          event_id: eventId,
          setting_key: key,
          setting_value: value,
          description: getSettingDescription(key)
        }));

        // Upsert event settings
        const { error } = await supabase
          .from('event_settings')
          .upsert(upsertData, { 
            onConflict: 'event_id,setting_key' 
          });

        if (error) throw error;
        
        toast.success('Event ticket settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getSettingDescription = (key: string) => {
    switch (key) {
      case 'ticket_base_amount':
        return 'Number of tickets for the first category a driver registers for';
      case 'ticket_additional_amount':
        return 'Number of tickets for each additional category';
      case 'ticket_max_amount':
        return 'Maximum total tickets a driver can receive regardless of categories';
      case 'wristband_per_category':
        return 'Number of wristbands per category';
      case 'wristband_max_amount':
        return 'Maximum total wristbands a driver can receive';
      case 'tool_car_max_amount':
        return 'Maximum number of tool cars a driver can register';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-white">Loading settings...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-zinc-900 rounded-lg p-6">
        {eventId && (
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-white">
                Event Ticket Settings
              </h2>
            </div>
            <button
              onClick={() => setTicketsEnabled(!ticketsEnabled)}
              className="flex items-center gap-2 text-white"
            >
              {ticketsEnabled ? (
                <>
                  <ToggleRight className="h-6 w-6 text-green-500" />
                  <span className="text-sm font-medium">Tickets Enabled</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="h-6 w-6 text-gray-500" />
                  <span className="text-sm font-medium">Tickets Disabled</span>
                </>
              )}
            </button>
          </div>
        )}

        {!ticketsEnabled && (
          <div className="mb-6 p-4 bg-amber-900/30 border border-amber-800 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 text-sm font-medium">Tickets Disabled for This Event</p>
                <p className="text-amber-300 text-sm mt-1">
                  This event will not use the ticket and wristband allocation system. Drivers will not receive any tickets or wristbands automatically.
                </p>
                <p className="text-amber-300 text-sm mt-2">
                  Tool car settings are also disabled for this event.
                </p>
              </div>
            </div>
          </div>
        )}

        {ticketsEnabled && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-xl font-semibold text-white">Ticket Allocation Rules</h2>
            </div>

            <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-blue-300 text-sm">
                  These settings control how tickets are allocated to drivers who register for multiple categories.
                  The first category a driver registers for gets the base amount of tickets, while additional categories
                  get the additional amount. The total is capped at the maximum amount.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Base Tickets (First Category)
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.ticket_base_amount}
                  onChange={(e) => setSettings({ ...settings, ticket_base_amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Number of tickets for the first category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Additional Tickets (Per Category)
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.ticket_additional_amount}
                  onChange={(e) => setSettings({ ...settings, ticket_additional_amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Number of tickets for each additional category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Maximum Total Tickets
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.ticket_max_amount}
                  onChange={(e) => setSettings({ ...settings, ticket_max_amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Maximum tickets regardless of categories
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-xl font-semibold text-white">Wristband Allocation Rules</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Wristbands Per Category
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.wristband_per_category}
                  onChange={(e) => setSettings({ ...settings, wristband_per_category: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Number of wristbands per category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Maximum Total Wristbands
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings.wristband_max_amount}
                  onChange={(e) => setSettings({ ...settings, wristband_max_amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Maximum wristbands regardless of categories
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maximum Tool Cars
              </label>
              <input
                type="number"
                min="0"
                max="5"
                value={settings.tool_car_max_amount}
                onChange={(e) => setSettings({ ...settings, tool_car_max_amount: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">
                Maximum number of tool cars a driver can register
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between items-center mt-6">
        <button
          onClick={fetchSettings}
          className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Reset
        </button>

        <button
          onClick={saveSettings}
          disabled={saving}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}